import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import ApplicationFormFooter from './ApplicationFormFooter';
import { postTestSchema } from "./postTest.validation";
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import PropTypes from 'prop-types';
import {
  ADMINISTRATIVE_ORIENTATION_QUESTIONS,
  MEDICARE_ORIENTATION_QUESTIONS,
  SAFETY_ORIENTATION_QUESTIONS,
  UNIVERSAL_PRECAUTIONS_QUESTIONS,
} from "./postTestConstants";

const mapTestAnswers = (test) => {
  if (!test) return null;
  const newAnswers = {};
  if (test.answers) {
    Object.keys(test.answers).forEach(qId => {
      newAnswers[qId] = {
        ...test.answers[qId],
        answer: test.answers[qId].answer === null ? "" : test.answers[qId].answer
      };
    });
  }
  return {
    ...test,
    answers: newAnswers,
    scoringResult: test.scoringResult || null
  };
};

const extractPostTestGroup = (resultData) => {
  if (!resultData) return null;
  if (resultData.postTestGroup) return resultData.postTestGroup;
  if (resultData.items && resultData.items.length > 0) return resultData.items[0];
  if (resultData.postTestGroups && resultData.postTestGroups.length > 0) return resultData.postTestGroups[0];
  if (Array.isArray(resultData) && resultData.length > 0) return resultData[0];
  if (resultData.id) return resultData;
  return null;
};

const PostTest = ({ positionName, onClose }) => {
  const [page, setPage] = useState(1);
  const [groupId, setGroupId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["testDate", "applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      let response;
      if (groupId) {
        payload.groupId = groupId;
        response = await request(`/post-tests/${groupId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/post-tests', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Post Test processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const methods = useForm({
    resolver: zodResolver(postTestSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      applicantName: "",
      testDate: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantSignature: "",
      applicantSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      tests: [
        {
          testType: "universal_precautions",
          testTitle: "Universal Precautions Post Test",
          answers: {},
        },
        {
          testType: "medicare_orientation",
          testTitle: "Medicare Orientation Post Test",
          answers: {},
        },
        {
          testType: "safety_orientation",
          testTitle: "Safety Orientation Post Test",
          answers: {},
        },
        {
          testType: "administrative_orientation",
          testTitle: "Administrative Orientation Post Test",
          answers: {},
        }
      ]
    },
    mode: "onChange"
  });

  const { handleSubmit, watch, formState: { errors } } = methods;

  const {
    targetUserId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    signerName,
    handleSignatureApply,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    handleStatusAction,
    handleSave,
    closeForm,
    actualFromId
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const isHeaderDisabled = isReadOnly || (!isAdmin && currentStatus === 'send_to_office');

  useEffect(() => {
    const fetchPostTest = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !groupId) return;

      try {
        if (!actualFromId && !targetUserId && !groupId) {
          if (currentUserData && !isAdmin) {
            methods.reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              testDate: parseIsoDateToMDYUS(getLocalSystemDate()),
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
              tests: methods.getValues().tests
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/post-tests/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/post-tests?userId=${targetUserId}&limit=1`);
          }
        } else {
          // Priority 1: Fetch by actualFromId (specific group)
          // Priority 2: Fetch by targetUserId (usually clinicians fetching their own)
          const fetchId = actualFromId || targetUserId;
          if (fetchId) {
            response = await request(`/post-tests/${fetchId}`);

            // If the above returns no group (e.g. standard lookup by userId returned a list or empty),
            // try the alternate query style like for admins.
            if (!response || !response.result || (!response.result.postTestGroup && !response.result.items)) {
              response = await request(`/post-tests?userId=${targetUserId}&limit=1`);
            }
          }
        }

        if (!response) return;

        const resultData = response.result || response;
        const group = extractPostTestGroup(resultData);

        if (group) {
          setGroupId(group.id);
          setCurrentStatus(group.status || "");

          const testsData = group.postTests || group.tests || [];

          const orderedTests = [
            mapTestAnswers(testsData.find(t => t.testType === 'universal_precautions')) || { testType: 'universal_precautions', answers: {}, scoringResult: null },
            mapTestAnswers(testsData.find(t => t.testType === 'medicare_orientation')) || { testType: 'medicare_orientation', answers: {}, scoringResult: null },
            mapTestAnswers(testsData.find(t => t.testType === 'safety_orientation')) || { testType: 'safety_orientation', answers: {}, scoringResult: null },
            mapTestAnswers(testsData.find(t => t.testType === 'administrative_orientation')) || { testType: 'administrative_orientation', answers: {}, scoringResult: null }
          ];

          methods.reset({
            tenantId: group.tenantId,
            userId: group.userId,
            applicantName: group.applicantName || group.orienteeName || "",
            testDate: parseIsoDateToMDYUS(group.testDate),
            applicantSignature: group.applicantSignature || "",
            ...getStandardizedSignatures(group, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(group, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(group, isAdmin).reviewedBySignedAt || ""),
            tests: orderedTests,
          });
        } else {
          console.warn("PostTest: Response received but no group extracted", resultData);
        }
      } catch (error) {
        console.error("Error fetching post test:", error);
      }
    };

    fetchPostTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
  }, [targetUserId, currentUserData?.id, actualFromId]);


  const handleOnNext = async () => {
    if (isAdmin || actualFromId || groupId) {
      setPage(p => Math.min(9, p + 1));
      return;
    }

    let questionsToValidate = [];
    let testIndex = 0;

    switch (page) {
      case 1:
        questionsToValidate = UNIVERSAL_PRECAUTIONS_QUESTIONS.slice(0, 8);
        testIndex = 0;
        break;
      case 2:
        questionsToValidate = UNIVERSAL_PRECAUTIONS_QUESTIONS.slice(8, 16);
        testIndex = 0;
        break;
      case 3:
        questionsToValidate = UNIVERSAL_PRECAUTIONS_QUESTIONS.slice(16);
        testIndex = 0;
        break;
      case 4:
        questionsToValidate = MEDICARE_ORIENTATION_QUESTIONS.slice(0, 10);
        testIndex = 1;
        break;
      case 5:
        questionsToValidate = MEDICARE_ORIENTATION_QUESTIONS.slice(10);
        testIndex = 1;
        break;
      case 6:
        questionsToValidate = SAFETY_ORIENTATION_QUESTIONS.slice(0, 13);
        testIndex = 2;
        break;
      case 7:
        questionsToValidate = SAFETY_ORIENTATION_QUESTIONS.slice(13);
        testIndex = 2;
        break;
      case 8:
        questionsToValidate = ADMINISTRATIVE_ORIENTATION_QUESTIONS.slice(0, 13);
        testIndex = 3;
        break;
      case 9:
        questionsToValidate = ADMINISTRATIVE_ORIENTATION_QUESTIONS.slice(13);
        testIndex = 3;
        break;
      default:
        break;
    }

    const fieldsToValidate = questionsToValidate.map(q => `tests.${testIndex}.answers.${q.id}.answer`);

    const result = await methods.trigger(fieldsToValidate);
    if (result) {
      setPage(p => Math.min(9, p + 1));
    } else {
      toast.error("Please answer all questions on this page.");
    }
  };

  // Helper to render questions
  const renderQuestion = (q, testIndex) => {
    const fieldName = `tests.${testIndex}.answers.${q.id}.answer`;
    const fieldError = errors?.tests?.[testIndex]?.answers?.[q.id]?.answer;

    return (
      <div key={q.id} className="mb-6">
        <input
          type="hidden"
          {...methods.register(`tests.${testIndex}.answers.${q.id}.type`)}
          value={q.type}
        />

        <p className="mb-2 font-medium text-gray-800">
          <strong>{q.id}.</strong> {q.text}
        </p>

        <div className="ml-6">
          {q.type === "true_false" && (
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="T"
                  {...methods.register(fieldName)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 mr-2"
                />
                <span>True</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="F"
                  {...methods.register(fieldName)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 mr-2"
                />
                <span>False</span>
              </label>
            </div>
          )}

          {q.type === "multiple_choice" && (
            <div className="space-y-2">
              {q.options?.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center cursor-pointer"
                >
                  <input
                    type="radio"
                    value={opt.value.toUpperCase()}
                    {...methods.register(fieldName)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}

          {q.type === "open_ended" && (
            <textarea
              {...methods.register(fieldName)}
              className={`w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldError ? 'border-red-500' : 'border-gray-300'}`}
              rows="2"
              placeholder="Enter your answer here..."
            />
          )}
          {fieldError && (
            <p className="text-red-500 text-xs mt-1 ml-6">{fieldError.message || "This field is required"}</p>
          )}
        </div>
      </div>
    );
  };

  const getPageTitle = () => {
    switch (page) {
      case 1:
        return "Post Test - Universal Precautions (Page 1/3)";
      case 2:
        return "Post Test - Universal Precautions (Page 2/3)";
      case 3:
        return "Post Test - Universal Precautions (Page 3/3)";
      case 4:
        return "Post Test - Medicare Orientation (Page 1/2)";
      case 5:
        return "Post Test - Medicare Orientation (Page 2/2)";
      case 6:
        return "Post Test - Safety Orientation (Page 1/2)";
      case 7:
        return "Post Test - Safety Orientation (Page 2/2)";
      case 8:
        return "Post Test - Administrative Orientation (Page 1/2)";
      case 9:
        return "Post Test - Administrative Orientation (Page 2/2)";
      default:
        return "Post Test Questionnaire";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl overflow-hidden">
        <EvaluationFormHeader
          title={getPageTitle()}
          subtitle="ORIENTATION ASSESSMENT"
        />
        <FormProvider {...methods}>
          {/* Common Header Info for all pages */}
          <div className="mb-0 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center p-8">
              <div>
                <EvaluationInput
                  name="applicantName"
                  label="Clinician / Orientee Name:"
                  placeholder="Name"
                  disabled={isHeaderDisabled}
                />
              </div>
              <div>
                <label htmlFor="testDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <InputDatePicker
                  formatSeparator="/"
                  name="testDate"
                  id="testDate"
                  max={getLocalSystemDate()}
                  disabled={isHeaderDisabled}
                />
              </div>
            </div>
          </div>
          <form
            className="space-y-6 p-8 min-h-[600px]"
            onSubmit={handleSubmit((d) => onSubmit(d, 'send_to_office'))}>
            <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />
            <fieldset disabled={isReadOnly} className="space-y-8">
              {/* Page 1: Universal Q1-8 */}
              {page === 1 && (
                <div className="mt-6">
                  {UNIVERSAL_PRECAUTIONS_QUESTIONS.slice(0, 8).map((q) =>
                    renderQuestion(q, 0),
                  )}
                </div>
              )}

              {/* Page 2: Universal Q9-16 */}
              {page === 2 && (
                <div className="mt-6">
                  {UNIVERSAL_PRECAUTIONS_QUESTIONS.slice(8, 16).map((q) =>
                    renderQuestion(q, 0),
                  )}
                </div>
              )}

              {/* Page 3: Universal Q17-25 */}
              {page === 3 && (
                <div className="mt-6">
                  {UNIVERSAL_PRECAUTIONS_QUESTIONS.slice(16).map((q) =>
                    renderQuestion(q, 0),
                  )}
                </div>
              )}

              {/* Page 4: Medicare Q1-10 */}
              {page === 4 && (
                <div className="mt-6">
                  {MEDICARE_ORIENTATION_QUESTIONS.slice(0, 10).map((q) =>
                    renderQuestion(q, 1),
                  )}
                </div>

              )}

              {/* Page 5: Medicare Q11-20 */}
              {page === 5 && (
                <div className="mt-6">
                  {MEDICARE_ORIENTATION_QUESTIONS.slice(10).map((q) =>
                    renderQuestion(q, 1),
                  )}
                </div>
              )}

              {/* Page 6: Safety Q1-13 */}
              {page === 6 && (
                <div>
                  <div className="mt-6">
                    {SAFETY_ORIENTATION_QUESTIONS.slice(0, 13).map((q) =>
                      renderQuestion(q, 2),
                    )}
                  </div>
                </div>
              )}

              {/* Page 7: Safety Q14-25 */}
              {page === 7 && (
                <div className="mt-6">
                  {SAFETY_ORIENTATION_QUESTIONS.slice(13).map((q) =>
                    renderQuestion(q, 2),
                  )}
                </div>
              )}

              {/* Page 8: Admin Q1-13 */}
              {page === 8 && (
                <div className="mt-6">
                  {ADMINISTRATIVE_ORIENTATION_QUESTIONS.slice(0, 13).map((q) =>
                    renderQuestion(q, 3),
                  )}
                </div>
              )}

              {/* Page 9: Admin Q14-24 + Sign */}
              {page === 9 && (
                <div className="mt-6">
                  {ADMINISTRATIVE_ORIENTATION_QUESTIONS.slice(13).map((q) =>
                    renderQuestion(q, 3),
                  )}

                  {isAdmin && (
                    <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                      <h3 className="text-xl font-bold mb-4 text-blue-800">Post Test Scoring Results</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {watch("tests")?.map((test) => (
                          <div key={test.testType} className="p-4 bg-white rounded shadow-sm border border-gray-100">
                            <h4 className="font-semibold text-gray-800 mb-2 border-b pb-1">{test.testTitle || test.testType?.replaceAll('_', ' ').toUpperCase()}</h4>
                            {test.scoringResult ? (
                              <div className="text-sm space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Score:</span>
                                  <span className="font-medium">{test.scoringResult.score} / {test.scoringResult.totalQuestions}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Correct:</span>
                                  <span className="text-green-600 font-medium">{test.scoringResult.correctAnswers}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Incorrect:</span>
                                  <span className="text-red-500 font-medium">{test.scoringResult.incorrectAnswers}</span>
                                </div>
                                <div className="mt-2 pt-2 border-t flex justify-between items-center">
                                  <span className="font-bold text-gray-700">Percentage:</span>
                                  <span className={`text-lg font-bold ${test.scoringResult.percentage >= 80 ? 'text-green-600' : 'text-orange-500'}`}>
                                    {test.scoringResult.percentage}%
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-500 italic text-center py-2">No score available</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </fieldset>

            <ApplicationFormFooter
              page={page}
              setPage={setPage}
              totalPages={9}
              methods={methods}
              onSave={handleSave}
              onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
              onStatusUpdate={handleStatusAction}
              onNext={handleOnNext}
              isSignatureModalOpen={isSignatureModalOpen}
              openSignatureModal={openSignatureModal}
              closeSignatureModal={closeSignatureModal}
              handleSignatureApply={handleSignatureApply}
              signerName={signerName}
              showCertification={false}
              requireSignatures={true}
              showSignatureSections={page === 9}
              applicantName={watch("applicantName") || ""}
              applicantSignature={watch("applicantSignature") || ""}
              applicantSignedAt={watch("applicantSignedAt") || ""}
              reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
              reviewedBySignature={watch("reviewedBySignature") || ""}
              reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
              submitLabel="Submit All"
              isReadOnly={isReadOnly}
              onClose={closeForm}
              currentStatus={currentStatus}
              skipValidation={isAdmin || actualFromId || groupId}
            />

          </form>
        </FormProvider>
      </div>

    </div >
  );
};

export default PostTest;

PostTest.propTypes = {
  positionName: PropTypes.string,
  onClose: PropTypes.func
};
